import { useCallback, useEffect, useRef, useState } from "react";
import { getEVInfo } from "../apis/parkingbeesRelatedService";
import type { IParkingBeesEvChargerVacancyResponse } from "../apis/parkingbeesRelatedService/type";

const POLL_MS = 3 * 60 * 1000;
const JITTER_MS = 60 * 1000;
const IDLE_MS = 2 * 60 * 1000;
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
  "pointerdown",
] as const;

export default function useEvInfoPolling() {
  const [evInfoList, setEvInfoList] = useState<
    IParkingBeesEvChargerVacancyResponse[]
  >([]);
  const [isInited, setIsInited] = useState(false);
  const mounted = useRef(true);
  const lastActiveAt = useRef(Date.now());
  const lastFetchedAt = useRef(0);

  const canPoll = useCallback(() => {
    if (document.hidden) return false;
    if (Date.now() - lastActiveAt.current >= IDLE_MS) return false;
    return true;
  }, []);

  const fetchAllEVInfo = useCallback(async () => {
    try {
      const evInfoResponse = await getEVInfo();
      lastFetchedAt.current = Date.now();
      if (mounted.current) setEvInfoList(evInfoResponse);
    } catch (error) {
      console.error(error);
    } finally {
      if (mounted.current) setIsInited(true);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    lastActiveAt.current = Date.now();
    fetchAllEVInfo();

    const markActive = () => {
      const wasIdle = Date.now() - lastActiveAt.current >= IDLE_MS;
      lastActiveAt.current = Date.now();
      if (wasIdle && canPoll()) {
        fetchAllEVInfo();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActive, { passive: true });
    });

    const onVisibilityChange = () => {
      if (!canPoll()) return;
      if (Date.now() - lastFetchedAt.current >= POLL_MS) {
        fetchAllEVInfo();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onVisibilityChange);

    const jitterMs = Math.floor(Math.random() * JITTER_MS);
    let intervalId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        if (!canPoll()) return;
        fetchAllEVInfo();
      }, POLL_MS);
    }, jitterMs);

    return () => {
      mounted.current = false;
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActive);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onVisibilityChange);
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [canPoll, fetchAllEVInfo]);

  return { evInfoList, isInited };
}
